import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Icon } from "./Icon";

/**
 * Workspace announcement banner. Shows active (un-expired) announcements the
 * current user hasn't dismissed; dismissing records a read receipt and clears
 * the banner. Rendered app-wide at the top of the content area.
 */
export function AnnouncementBanner() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: queryKeys.announcements(),
    queryFn: api.announcements,
    staleTime: 60_000,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.dismissAnnouncement(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.announcements() }),
  });

  const active = (q.data?.announcements ?? []).filter((a) => !a.dismissed);
  if (active.length === 0) return null;

  return (
    <div className="anc-stack">
      {active.map((a) => (
        <div key={a.id} className={`anc-banner${a.pinned ? " pinned" : ""}`}>
          <Icon name={a.pinned ? "pin" : "bell"} size={14} />
          <div className="anc-text">
            <span className="anc-title">{a.title}</span>
            {a.body && <span className="anc-body">{a.body}</span>}
          </div>
          <button
            type="button"
            className="anc-dismiss"
            aria-label={`Dismiss announcement: ${a.title}`}
            onClick={() => dismiss.mutate(a.id)}
            disabled={dismiss.isPending}
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
